// ToDo: WW-7459 Move to shared shadow folder
import styles from '../../../../../../erp/components/tab-panels/positions/components/positions-table/PositionsTable.module.scss';

import { Box, Tooltip } from '@mui/material';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableInstance } from 'react-table';

import { Article } from '@work4all/models/lib/Classes/Article.entity';
import { ServiceContractPosition } from '@work4all/models/lib/Classes/ServiceContractPosition.entity';
import { AddServiceContractPosTypeOfPos } from '@work4all/models/lib/Enums/AddServiceContractPosTypeOfPos.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { ServiceContractPositionKind } from '@work4all/models/lib/Enums/ServiceContractPositionKind.enum';

import { formatNumberAsCurrency } from '@work4all/utils';

import { settings, useSetting } from '../../../../../../../../../../settings';
// ToDo: WW-7459 Move to shared shadow folder (2 below)
import { useSelectedPositionState } from '../../../../../../erp/components/tab-panels/positions/components/hooks/use-selected-position';
import { handleOrderChange } from '../../../../../../erp/components/tab-panels/positions/components/utils/handle-order-change';
import { ShadowServiceContractObjectAddComponentArgs } from '../../../../../hooks/use-shadow-service-contract-object-api';
import { useShadowServiceContractObjectApiContext } from '../../../../../hooks/use-shadow-service-contract-object-api-context';
import { EditTable } from '../edit-table/EditTable';
import {
  EditTableColumns,
  EditTableEntry,
  EditTableProps,
  IdArray,
  IEditTable,
  OnEditComponent,
} from '../edit-table/types';
import { PositionMask } from '../position-mask/PositionMask';

import { PositionsMobileTable } from './PositionsMobileTable';

export const ForbiddenPriceCell: React.FC = () => {
  const { t } = useTranslation();
  return (
    <Tooltip title={t('ALERTS.NOT_AUTHORIZED_TO_SEE_PRICES')}>
      <Box textAlign="center">-</Box>
    </Tooltip>
  );
};

export type ForwardedEditTableProps = Pick<
  EditTableProps<ServiceContractPosition>,
  'showSelectionColumn' | 'refs' | 'showBorders' | 'allowedColumns'
>;

export type IPositionsTableProps = {
  disabled?: boolean;
  showPositionMask?: boolean;
  positions: ServiceContractPosition[];
  onMoveComponent: (localId: string, index: number) => void;
  onPickPosition?: (article?: Article) => void;
  onRemoveComponent: (positionIds: IdArray) => void;
  onAddComponent?: (props: ShadowServiceContractObjectAddComponentArgs) => void;
  onEditComponent: (result: OnEditComponent<ServiceContractPosition>) => void;
  tableInstanceRef: React.RefObject<TableInstance | null>;
  columns: EditTableColumns<ServiceContractPosition>[];
  refs?: {
    editTable: React.RefObject<IEditTable>;
  };
  total: number;
  singleLine?: boolean;
} & ForwardedEditTableProps;

export const PositionsTable = (props: IPositionsTableProps) => {
  const {
    disabled = false,
    positions,
    onAddComponent,
    onEditComponent,
    showPositionMask = true,
    showBorders = true,
    columns,
    allowedColumns,
    showSelectionColumn,
    singleLine = true,
    total,
  } = props;

  const { t } = useTranslation();
  const { pending, loading } = useShadowServiceContractObjectApiContext();

  const { setSelectedPositionIds } = useSelectedPositionState();

  const renderMobileFooter = useCallback(() => {
    return (
      <tr className={clsx(styles.title, styles.mobilePositionRow)}>
        <td></td>
        <td></td>
        <td></td>
        <td className={clsx(styles['tr-border'], styles.mobilePositionRow)}>
          <span style={{ float: 'right' }}>
            {formatNumberAsCurrency(total)}
          </span>
          <div style={{ whiteSpace: 'pre-line' }}>{t('COMMON.TOTAL')}</div>
        </td>
      </tr>
    );
  }, [total, t]);

  const classes = useMemo(
    () => ({
      mobile: styles.mobilePositionsTable,
      thead: styles.thead,
      tbody: styles.tbody,
      tfoot: clsx(styles.tfoot, styles.noDragTfoot, {
        [styles.disabledTfoot]: disabled,
      }),
    }),
    [disabled]
  );

  const loadingRows = useMemo(
    () =>
      (pending.type === 'modifyPosition' &&
        ['price', 'amountDecimal', 'discount'].some((x) =>
          pending.properties.includes(x)
        )) ||
      pending.type === 'movePosition'
        ? [pending.positionId]
        : [],
    [pending]
  );

  const isRowEditable = useMemo(
    () => !pending.pending && pending.type !== 'addPosition',
    [pending]
  );

  const decorators = useMemo(() => {
    const isEditableCell = (
      cell: string,
      row?: ServiceContractPosition & {
        typeOfPosition?: AddServiceContractPosTypeOfPos;
      }
    ) => {
      if (cell === 'number') {
        return false;
      }

      // For HEADLINE positions, only allow 'longtext' field to be editable
      if (row?.positionKind === ServiceContractPositionKind.UEBERSCHRIFT) {
        return cell === 'longtext';
      }

      if (cell === 'amountDecimal') {
        if (row?.typeOfPosition === AddServiceContractPosTypeOfPos.DEFAULT) {
          return false;
        }
        return true;
      }

      return true;
    };

    return { isEditableCell };
  }, []);

  const onRowOut = useCallback(
    (position: ServiceContractPosition, dir: 1 | -1) => {
      const notCachedPositions = positions.filter(
        (x) => !(x as EditTableEntry).cacheOnly
      );
      const isLast =
        notCachedPositions[notCachedPositions.length - 1]?.id === position?.id;
      if (isLast) {
        if (showBorders)
          onAddComponent({
            typeOfPosition: AddServiceContractPosTypeOfPos.DEFAULT,
            article: { id: 0 } as Article,
          });
        setTimeout(() => {
          props.refs.editTable.current.setEditMode('undefined', 'longtext', [
            'longtext',
          ]);
        }, 100);
      } else {
        const positionIndex = notCachedPositions.findIndex(
          (x) => x.id === position?.id
        );
        const nextPosition = notCachedPositions[positionIndex + dir];
        if (nextPosition)
          props.refs.editTable.current.setEditMode(
            nextPosition.id.toString(),
            'longtext'
          );
      }
      // scroll to the left to see longtext popover
      if (props.refs.scroll?.current) props.refs.scroll.current.scrollLeft = 0;
    },
    [
      positions,
      props.refs.scroll,
      props.refs.editTable,
      showBorders,
      onAddComponent,
    ]
  );
  const serviceContractSize = useSetting(
    settings.serviceContractPositionsRowSize()
  );

  const columnSettings = useSetting(settings.serviceContractPositionsColumn());
  const noIdEditableRows = useMemo(
    () => ['longtext', 'amountDecimal', 'shortText', 'unit', 'internalText'],
    []
  );

  const editableRows = useMemo(() => {
    const editableLongtex = ['longtext'];
    const defaultEditable = ['longtext', 'amountDecimal'];

    return {
      HEADLINE: editableLongtex,
      TEXTLINE: editableLongtex,
      TEXTZEILE: editableLongtex,
      DEFAULT: defaultEditable,
    };
  }, []);

  const renderMobilePosition = useCallback(
    (
      position: ServiceContractPosition,
      setEditedPosition: (position: ServiceContractPosition) => void,
      allPositions: ServiceContractPosition[]
    ) => {
      return (
        <PositionsMobileTable
          position={position}
          setEditedPosition={setEditedPosition}
          allPositions={allPositions}
          columns={columns}
          decorators={decorators}
          editableRows={editableRows}
        />
      );
    },
    [columns, decorators, editableRows]
  );

  return !loading ? (
    <Box display="flex" flex="1" flexDirection="column" overflow="hidden">
      <EditTable<ServiceContractPosition>
        // Editable state
        onAddComponent={props.onAddComponent}
        onRemoveComponent={props.onRemoveComponent}
        onEditComponent={onEditComponent}
        onMoveComponent={props.onMoveComponent}
        onDropItem={props.onPickPosition}
        // updateMode="cell"
        // refs
        ref={props.refs.editTable}
        refs={props.refs}
        tableInstanceRef={props.tableInstanceRef}
        // mask & mobile
        renderMobileItem={renderMobilePosition}
        renderMobileFooter={renderMobileFooter}
        mask={showPositionMask ? PositionMask : undefined}
        // Others
        disabled={disabled}
        columns={columns}
        items={positions}
        editableRows={editableRows}
        isRowEditable={isRowEditable}
        onSelectedItemIdsChange={setSelectedPositionIds}
        loadingRows={loadingRows}
        itemSize="auto"
        noIdEditableCells={noIdEditableRows}
        decorators={decorators}
        showBorders={showBorders}
        showSelectionColumn={showSelectionColumn}
        allowedColumns={allowedColumns}
        onRowOut={onRowOut}
        columnSettings={columnSettings}
        handleOrderChange={handleOrderChange}
        rowSizeMode={singleLine ? serviceContractSize.value : 'AUTO'}
        entity={Entities.serviceContractPosition}
        bordersKey="SERVICE_CONTRACT"
        classes={classes}
      />
    </Box>
  ) : null;
};
